import { inject, Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { Utilities } from "@tylertech/shared-module";
import { IGridViewMetadata, IProjectionGridMetadata, IViewMetadata, KeyValuePair, PropertyMetadata } from "@tylertech/tyler-api-types";
import
{
    CachedCodeLookupService,
    ResetStateService,
    GridMetadataService,
    MetadataInputTypeHelperService,
    MetadataService,
    MetadataViewService,
    ProviderStorageService,
    GridProcessingService,
    GridFetchType,
    GridSelectionService,
} from "../../services";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { CachedRepositoryService, NavigationService } from "@tylertech/core-module";
import { EntityWrapper, LazyLoadedProxyHandler, MetadataKey, RepositoryFormData } from "../../interfaces";
import { ObjectHelpers } from "../../utilities";

@Injectable({ providedIn: "root" })
export class GridEntityService extends ResetStateService
{
    private $odataFilterFactory = inject(ODataFilterFactoryService);
    private $cachedCodeLookupService = inject(CachedCodeLookupService);
    private $repositoryService = inject(CachedRepositoryService);
    private $providerStorageService = inject(ProviderStorageService);
    private $metadataService = inject(MetadataService);
    private $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    private $metadataViewService = inject(MetadataViewService);
    private $gridMetadataService = inject(GridMetadataService);
    private $gridProcessingService = inject(GridProcessingService);
    private $gridSelectionService = inject(GridSelectionService);


    UpdatedEntities: Record<string, any[]> = {};

    constructor()
    {
        super(inject(NavigationService));
    }

    fetchRowModel(row: any, datacontroller: RepositoryFormData, viewMetadata: IGridViewMetadata): Promise<any>
    {
        if (viewMetadata && "RowTypeName" in viewMetadata.GridProperties)
        {
            const rowTypeName = viewMetadata.GridProperties["RowTypeName"] as string;
            const idProperty = viewMetadata.GridProperties["IdProperty"];
            return datacontroller.findExistingOrGet(rowTypeName, idProperty, row.Id, this.$repositoryService)
                .then(entity =>
                {
                    if (!entity["$isEntityWrapper"])
                    {
                        const modelWrapper = new EntityWrapper(entity, this.$metadataService);
                        entity = new Proxy(modelWrapper, new LazyLoadedProxyHandler(this.$metadataService));
                    }
                    datacontroller.Add(rowTypeName, entity);
                    entity.$$TypeName = rowTypeName;
                    if (!entity.$$Parent)
                        entity.$$Parent = row["$$model"].$$Parent;
                    return entity;
                });
        }
        return Promise.resolve(row["$$model"]);
    }

    updateProjectionTableEntity(providerId: string, entities: any[], viewMetadata: IGridViewMetadata): Promise<void>
    {
        this.updateEntities(providerId, entities);
        const idProperty = viewMetadata.GridProperties.IdProperty;
        const entity = entities[0];
        const doLocalMerge: boolean = !viewMetadata.EditForm?.RecalculateParentFormOnSave && this.$providerStorageService.GridFetchType[providerId] !== GridFetchType.ServerStore;
        const promises: Promise<void>[] = [];
        //item was added earlier and now we are editing it
        const editingAdded = !Utilities.IsNullOrUndefinedOrZero(entity["$$AddedRowId"]) && this.$providerStorageService.LocalEntitiesStore[providerId].some(x => x[idProperty] === entity["$$AddedRowId"]);

        if (entity.SerializedRowState === "Deleted")
        {
            const toDeleteRows = this.$providerStorageService.GridData[providerId].filter(x => x["$$Deleting"]);
            const toDelete = toDeleteRows.length >= 1 ? toDeleteRows[0] : undefined;

            if (!Utilities.IsNullOrUndefined(toDelete) && !Utilities.IsNullOrUndefined(toDelete.Id))
            {
                const index = this.$providerStorageService.GridData[providerId].indexOf(toDelete);
                this.$providerStorageService.GridData[providerId].splice(index, 1);
                this.$providerStorageService.TotalHits[providerId]--;

                if (this.$providerStorageService.GridFetchType[providerId] !== GridFetchType.ServerStore)
                {
                    toDeleteRows.forEach(x => this.removeDeleted(x, idProperty, this.$providerStorageService.LocalEntitiesStore[providerId]));
                    toDeleteRows.forEach(x => this.removeDeleted(x, idProperty, this.$providerStorageService.FilteredEntities[providerId]));
                }
            }
        }
        else if (entity.SerializedRowState === "Added" && !editingAdded)
        {

            let entityToAdd = { SerializedRowState: "Added", $$TypeName: viewMetadata.GridProperties.ClassMetadata.TypeName, $$editEntity : entity};
            const modelWrapper = new EntityWrapper(entityToAdd, this.$metadataService);
            entityToAdd = new Proxy(modelWrapper, new LazyLoadedProxyHandler(this.$metadataService));
            promises.push(...this.updateRowModel(viewMetadata, entity, entityToAdd));
            if (doLocalMerge)
            {
                this.$providerStorageService.LocalEntitiesStore[providerId] = this.$providerStorageService.LocalEntitiesStore[providerId] ?? [];
                this.$providerStorageService.LocalEntitiesStore[providerId].push(entityToAdd);
            }
        }
        else if (entity["$added"] && !editingAdded) //added and already saved, so rowstate is unchanged and entity is now a real entity
        {
            const gridProperties = (<IProjectionGridMetadata>viewMetadata.GridProperties);
            if (gridProperties.FieldMapChild)
            {
                let entityToAdd = entity[gridProperties.FieldMapChild];
                if (!entityToAdd["$isEntityWrapper"])
                {
                    const modelWrapper = new EntityWrapper(entityToAdd, this.$metadataService);
                    entityToAdd = new Proxy(modelWrapper, new LazyLoadedProxyHandler(this.$metadataService));
                }
                if (doLocalMerge)
                {
                    this.$providerStorageService.LocalEntitiesStore[providerId] = this.$providerStorageService.LocalEntitiesStore[providerId] ?? [];
                    this.$providerStorageService.LocalEntitiesStore[providerId].push(entityToAdd);
                }
            }
            else
            {
                const entityToAdd = { SerializedRowState: "Unchanged", $$TypeName: viewMetadata.GridProperties.ClassMetadata.TypeName};
                entityToAdd[gridProperties.IdProperty] = entity[gridProperties.IdProperty];
                promises.push(...this.updateRowModel(viewMetadata, entity, entityToAdd));
                if (doLocalMerge)
                {
                    this.$providerStorageService.LocalEntitiesStore[providerId] = this.$providerStorageService.LocalEntitiesStore[providerId] ?? [];
                    this.$providerStorageService.LocalEntitiesStore[providerId].push(entityToAdd);
                }
            }
        }
        else
        {
            const row = this.$providerStorageService.GridData[providerId]?.find(x => x["$$model"] && x["$$model"]["$$Editing"]);
            if (row)
            {
                const rowModel = row["$$model"];
                promises.push(...this.updateRowModel(viewMetadata, entity, rowModel));
                delete row["$$model"]["$$Editing"];
            }
        }
        this.fireModelChangedListeners(providerId);
        delete viewMetadata["$projectionGridUpdatingEntity"];
        return Promise.all(promises).then(() =>
        {
            return;
        });
    }

    private updateEntities(providerId: string, entities: any[]): void
    {
        if (Utilities.IsNullOrUndefined(this.UpdatedEntities[providerId])) this.UpdatedEntities[providerId] = [];
        this.UpdatedEntities[providerId].push(...entities);
    }

    syncPaged(providerId: string, viewMetadata: IGridViewMetadata): void
    {
        this.$gridSelectionService.syncPagedSelections(providerId, viewMetadata);
        const self = this;
        if (
            !viewMetadata.GridProperties.IdProperty ||
            !this.UpdatedEntities[providerId]
        )
            return;
        this.UpdatedEntities[providerId].forEach(entity =>
        {
            const row = self.$providerStorageService.GridData[providerId].filter(
                x => x.Id === entity[viewMetadata.GridProperties.IdProperty]
            )[0];
            if (!!row && entity.SerializedRowState === "Deleted")
            {
                const index = self.$providerStorageService.GridData[providerId].indexOf(row);
                if (index > -1) self.$providerStorageService.GridData[providerId].splice(index, 1);
                self.$providerStorageService.TotalHits[providerId]--;
            }
            else if (entity.SerializedRowState === "Added")
            {
                const index = this.$providerStorageService.GridData[providerId].length;
                const row = this.$gridProcessingService.createDataRow(
                    entity,
                    index,
                    viewMetadata,
                    null
                );
                if (this.$providerStorageService.GridData[providerId].length > 0)
                    this.$providerStorageService.GridData[providerId][0] = row;
                else this.$providerStorageService.GridData[providerId].push(row);
                self.$providerStorageService.TotalHits[providerId]++;
            }
            else if (row)
            {
                viewMetadata["$columns"].forEach(column =>
                {
                    if (entity[column.Name] !== undefined)
                        row[column.Name] = self.$metadataService.getFormattedValueFromProp(
                            entity[column.Name],
                            self.$gridMetadataService.getProperty(
                                viewMetadata.GridProperties.ClassMetadata,
                                column.Name
                            )
                        );
                });
            }
        });
        this.fireModelChangedListeners(providerId);
    }

    updateRowModel(viewMetadata: IGridViewMetadata, entity: any, rowModel: any): Promise<void>[]
    {
        const projectionGridProperties = (<IProjectionGridMetadata>viewMetadata.GridProperties);
        const promises: Promise<void>[] = [];
        viewMetadata["$columns"].forEach(column =>
        {
            let entityField;
            if (projectionGridProperties.FieldMap && projectionGridProperties.FieldMap[column.Name])
                entityField = projectionGridProperties.FieldMap[column.Name];
            else if (entity[column.Name] !== undefined)
                entityField = column.Name;
            else if (projectionGridProperties.FieldMapChild)
                entityField = `${projectionGridProperties.FieldMapChild}.${column.Name}`;
            if (entityField)
            {
                let propertyMetadata: PropertyMetadata;
                viewMetadata.EditForm.Views.forEach(v =>
                {
                    if (!propertyMetadata)
                        propertyMetadata = this.getPropertyMetadataFromEditForm(v, entityField);
                });
                if (!propertyMetadata)
                    propertyMetadata = this.$gridMetadataService.getProperty(viewMetadata.GridProperties.ClassMetadata, entityField);
                if (propertyMetadata)
                {
                    if (this.$metadataInputTypeHelperService.isLookup(propertyMetadata.InputType))
                    {
                        if (propertyMetadata.LookupProperty)
                        {
                            const list: KeyValuePair<MetadataKey, string>[] = this.$metadataService.DependentModuleValue(propertyMetadata.LookupProperty, entity["$$Parent"], entity);
                            if (!Utilities.IsNullOrUndefinedOrEmpty(list))
                            {
                                promises.push(Promise.resolve()
                                    .then(() =>
                                    {
                                        const match = list.find(x => x.Key.toString() === entity[entityField]?.toString());
                                        if (match)
                                            rowModel[column.Name] = match.Value;
                                    })
                                );
                            }

                        }
                        if (propertyMetadata.Lookup)
                        {
                            const builder = this.$odataFilterFactory.createBuilder();
                            builder.filterEQ("Key", entity[entityField]?.toString());
                            promises.push(this.$cachedCodeLookupService.Get(propertyMetadata.Lookup, builder.build())
                                .then(x =>
                                    {
                                        if (x.length === 1)
                                            rowModel[column.Name] = x[0].Value;
                                    }));
                        }
                    }
                    else
                    {
                        let value: any;
                        if (entityField.includes("."))
                        {
                            const segments = entityField.split(".");
                            const child = segments[0];
                            entityField = segments[1];
                            value = entity[child][entityField];
                        }
                        else
                            value = entity[entityField];
                        rowModel[column.Name] = value;
                    }

                }
            }
        });
        return promises;
    }

    removeDeleted(toDelete: any, idProperty: string, list: any[]): void
    {
        const modelToDelete = toDelete["$$model"];
        const existingRow = list.find(row => row[idProperty] === modelToDelete[idProperty]);
        //TODO: this is some weird casing of finding a record due to potential lack of an Id...need to shore this up
        let index = list.indexOf(existingRow);
        if (index >= 0)
            list.splice(index, 1);
        index = list.indexOf(toDelete);
        if (index >= 0)
            list.splice(index, 1);
        index = list.indexOf(list.find(x => ObjectHelpers.GetId(idProperty, x, 0) === toDelete.Id));
        if (index >= 0)
            list.splice(index, 1);
        if (toDelete["$$model"]["IsAddMode"] === true)
        {
            const index = list.indexOf(toDelete);
            if (index >= 0)
                list.splice(index, 1);
        }
    }

    private getPropertyMetadataFromEditForm(viewMetadata: IViewMetadata, property: string): PropertyMetadata
    {
        let propertyMetadata: PropertyMetadata;
        if (this.$metadataViewService.IsCombinedView(viewMetadata))
        {
            viewMetadata.Views.forEach(v =>
            {
                if (!propertyMetadata)
                    propertyMetadata = this.getPropertyMetadataFromEditForm(v, property);
                else
                    return propertyMetadata;
            });
        }
        else if (viewMetadata?.ViewMetaData?.Properties)
        {
            if (property.includes("."))
            {
                const segments = property.split(".");
                const child = segments[0];
                property = segments[1];
                const childProperty = propertyMetadata = viewMetadata.ViewMetaData.Properties.find(p => p.Name === child);
                if (childProperty?.ClassMetadata)
                    propertyMetadata = childProperty.ClassMetadata.Properties.find(p => p.Name === property);
            }
            else
                propertyMetadata = viewMetadata.ViewMetaData.Properties.find(p => p.Name === property);
        }

        return propertyMetadata;
    }

    private readonly modelChanged$ = new Subject<string>();

    get onModelChanged(): Observable<string>
    {
        return this.modelChanged$.asObservable();
    }

    fireModelChangedListeners(providerId: string): void
    {
        this.modelChanged$.next(providerId);
    }
}
